'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import logo from '../../assets/logo.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faXmark } from '@fortawesome/free-solid-svg-icons';

const NAV_LINKS = [
  { label: 'Product', anchor: 'features', href: null },
  { label: 'Pricing', anchor: 'pricing', href: null },
  { label: 'About', anchor: 'about', href: null },
  { label: 'Blog', anchor: null, href: '/blog' },
];

export function LandingNav({ solid }: { solid?: boolean } = {}) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const onHome = pathname === '/home';

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const isWhite = solid || scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        isWhite
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-[#e2e8f0]'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link href="/home" className="flex items-center gap-2.5 flex-shrink-0">
          <Image src={logo} alt="Apporb" width={32} height={32} className="rounded-xl" />
          <span className="font-bold text-[#0f172a] text-base tracking-tight">Apporb</span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((link) => {
            if (link.href) {
              return (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-sm font-medium text-[#475569] hover:text-[#1d4ed8] transition-colors"
                >
                  {link.label}
                </Link>
              );
            }
            const href = onHome ? `#${link.anchor}` : `/home#${link.anchor}`;
            return (
              <a
                key={link.label}
                href={href}
                className="text-sm font-medium text-[#475569] hover:text-[#1d4ed8] transition-colors"
              >
                {link.label}
              </a>
            );
          })}
        </div>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language selector — hidden for now */}
          {false && <select
            aria-label="Language"
            className="text-xs text-[#475569] bg-transparent border border-[#e2e8f0] rounded-lg px-2 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]/20"
          >
            <option value="en">🇺🇸 EN</option>
            <option value="de">🇩🇪 DE</option>
            <option value="fr">🇫🇷 FR</option>
            <option value="es">🇪🇸 ES</option>
            <option value="ja">🇯🇵 JA</option>
          </select>}
          <Link
            href="/login"
            className="text-sm font-medium text-[#1d4ed8] hover:text-[#1e3a8a] transition-colors px-3 py-1.5"
          >
            Log in
          </Link>
          <Link
            href="/accounts/new"
            className="text-sm font-bold text-white px-4 py-2 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-px transition-all"
            style={{ background: '#1d4ed8' }}
          >
            Start Free Trial
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          className="md:hidden p-2 text-[#475569] hover:text-[#1d4ed8]"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {menuOpen ? (
            <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
          ) : (
            <FontAwesomeIcon icon={faBars} className="w-5 h-5" />
          )}
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-b border-[#e2e8f0] px-6 py-4 space-y-3">
          {NAV_LINKS.map((link) => {
            if (link.href) {
              return (
                <Link
                  key={link.label}
                  href={link.href}
                  className="block text-sm font-medium text-[#475569] hover:text-[#1d4ed8] py-1"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              );
            }
            const href = onHome ? `#${link.anchor}` : `/home#${link.anchor}`;
            return (
              <a
                key={link.label}
                href={href}
                className="block text-sm font-medium text-[#475569] hover:text-[#1d4ed8] py-1"
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </a>
            );
          })}
          <div className="pt-2 flex flex-col gap-2">
            <Link href="/login" className="text-center text-sm font-medium text-[#1d4ed8] border border-[#bfdbfe] rounded-xl py-2 hover:bg-[#eff6ff] transition-colors">
              Log in
            </Link>
            <Link
              href="/accounts/new"
              className="text-center text-sm font-bold text-white rounded-xl py-2"
              style={{ background: '#1d4ed8' }}
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
